import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Page,
  Card,
  Text,
  TextField,
  Button,
  Checkbox,
  Select,
  Modal,
  InlineStack,
  BlockStack,
  Box,
  Badge,
  Divider,
} from "@shopify/polaris";
import LicenseExpiredOverlay from "../components/LicenseExpiredOverlay";

function normalizeId(rawId) {
  if (!rawId) return "";
  const value = String(rawId).trim();
  const match = value.match(/\/(\d+)$/);
  return match ? match[1] : value;
}

function normalizeShopDomain(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";
  return value.endsWith(".myshopify.com") ? value : `${value}.myshopify.com`;
}

function decodeShopFromHostParam(hostParam) {
  try {
    if (!hostParam) return "";
    const decoded = atob(hostParam);
    const url = decoded.startsWith("http")
      ? new URL(decoded)
      : new URL(`https://${decoded}`);

    const queryShop = url.searchParams.get("shop");
    if (queryShop) return normalizeShopDomain(queryShop);

    if (url.hostname && url.hostname.endsWith(".myshopify.com")) {
      return url.hostname;
    }

    const match = url.pathname.match(/\/store\/([^/]+)/i);
    if (match?.[1]) return normalizeShopDomain(match[1]);
  } catch {
    // ignore
  }
  return "";
}

const SHOP_STORAGE_KEY = "rental_shop_domain";

function readStoredShop() {
  try {
    return sessionStorage.getItem(SHOP_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function writeStoredShop(value) {
  try {
    if (value) sessionStorage.setItem(SHOP_STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

function getShopFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const shopParam = params.get("shop");
    if (shopParam) {
      const normalized = normalizeShopDomain(shopParam);
      writeStoredShop(normalized);
      return normalized;
    }

    const hostParam = params.get("host");
    if (hostParam) {
      const decoded = decodeShopFromHostParam(hostParam);
      if (decoded) {
        writeStoredShop(decoded);
        return decoded;
      }
    }

    return readStoredShop();
  } catch {
    return readStoredShop();
  }
}

function extractReauthUrl(data) {
  if (!data) return "";
  const direct = typeof data.reauthUrl === "string" ? data.reauthUrl : "";
  if (direct) return direct;

  const detail = String(data.detail || data.message || "");
  const match = detail.match(/(\/auth\?shop=[^\s]+)/);
  return match ? match[1] : "";
}

function buildReauthUrl(rawUrl) {
  const value = String(rawUrl || "").trim();
  if (!value) return "";

  try {
    const url = new URL(value, window.location.origin);
    if (!url.searchParams.get("returnTo")) {
      url.searchParams.set(
        "returnTo",
        `${window.location.pathname}${window.location.search}`,
      );
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

const CONFIGURED_PRODUCTS_KEY = "configuredRentalProducts";

function readConfiguredProducts() {
  try {
    const raw = localStorage.getItem(CONFIGURED_PRODUCTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeConfiguredProducts(products) {
  try {
    localStorage.setItem(CONFIGURED_PRODUCTS_KEY, JSON.stringify(products));
  } catch {
    // ignore
  }
}

const STATUS_PILL_STYLES = {
  total: { background: "#dbeafe", color: "#1e3a8a" },
  available: { background: "#dcfce7", color: "#166534" },
  on_rent: { background: "#fef9c3", color: "#854d0e" },
  maintenance: { background: "#ffedd5", color: "#9a3412" },
  reserved: { background: "#e0e7ff", color: "#3730a3" },
  sold: { background: "#fee2e2", color: "#991b1b" },
  item_active: { background: "#ccfbf1", color: "#115e59" },
  item_inactive: { background: "#fce7f3", color: "#9d174d" },
  visibility_visible: { background: "#ede9fe", color: "#5b21b6" },
  visibility_hidden: { background: "#e5e7eb", color: "#374151" },
};

function getPillStyle(kind) {
  return STATUS_PILL_STYLES[kind] || { background: "#f3f4f6", color: "#111827" };
}

function StatusPill({ kind, children }) {
  const colors = getPillStyle(kind);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: "999px",
        fontSize: "13px",
        lineHeight: "20px",
        background: colors.background,
        color: colors.color,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function formatItemTypeLabel(value) {
  const v = String(value || "").toLowerCase();
  if (v === "non_inventory") return "Non Inventory";
  if (v === "inventory") return "Inventory";
  if (v === "serial") return "Serial";
  if (v === "lot") return "Lot";
  return value || "-";
}

function formatRentalStatusLabel(value) {
  const label = String(value || "").trim();
  if (!label) return "-";
  if (label.toLowerCase() === "reserved") return "Pending Fulfillment";
  return label;
}

function getSkuDisplay(row) {
  const sku = String(row?.sku || "").trim();
  if (sku && sku !== "-") return sku;
  const variantId = String(row?.variantNumericId || "").trim();
  if (variantId) return variantId;
  return "-";
}

function getCurrencySymbol(code) {
  const currencyCode = String(code || "").trim().toUpperCase();
  if (!currencyCode) return "$";
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbolPart = parts.find((part) => part.type === "currency");
    return symbolPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
}

function formatMoneyValue(value, symbol) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  return `${symbol}${safe.toFixed(2)}`;
}

function RateCalendarIcon({ color = "#2563eb" }) {
  return (
    <span
      style={{
        width: "20px",
        height: "20px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color,
      }}
    >
      <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="14" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 7.5H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M7 2.8V5.2M13 2.8V5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="10" cy="11.8" r="1.1" fill="currentColor" />
      </svg>
    </span>
  );
}

const SERIAL_SUMMARY_META = [
  { key: "total", label: "Total Items", icon: "📦", bg: "#f7f9ff", color: "#1e3a8a", border: "#c7d2fe", accent: "#5b8cff", shadow: "rgba(91, 140, 255, 0.28)" },
  { key: "available", label: "Available", icon: "✅", bg: "#f3fffd", color: "#0f766e", border: "#99f6e4", accent: "#14b8a6", shadow: "rgba(20, 184, 166, 0.28)" },
  { key: "on_rent", label: "On Rent", icon: "🕒", bg: "#fffdf5", color: "#9a5b00", border: "#fde68a", accent: "#f59e0b", shadow: "rgba(245, 158, 11, 0.3)" },
  { key: "maintenance", label: "Maintenance", icon: "🛠️", bg: "#fff8f3", color: "#c2410c", border: "#fdba74", accent: "#f97316", shadow: "rgba(249, 115, 22, 0.3)" },
  { key: "reserved", label: "Pending Fulfillment", icon: "🔖", bg: "#f7f7ff", color: "#3730a3", border: "#c4b5fd", accent: "#818cf8", shadow: "rgba(129, 140, 248, 0.3)" },
  { key: "sold", label: "Sold", icon: "💸", bg: "#fff6f6", color: "#b91c1c", border: "#fca5a5", accent: "#ef4444", shadow: "rgba(239, 68, 68, 0.3)" },
];

function SerialSummaryCards({ summary, hideReserved = false }) {
  const items = hideReserved
    ? SERIAL_SUMMARY_META.filter((item) => item.key !== "reserved")
    : SERIAL_SUMMARY_META;

  return (
    <div
      style={{
        overflowX: "auto",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${items.length}, minmax(140px, 1fr))`,
          gap: "10px",
          minWidth: hideReserved ? "760px" : "900px",
        }}
      >
        {items.map((item) => (
          <div
            key={item.key}
            style={{
              border: `1px solid ${item.border}`,
              borderRadius: "12px",
              backgroundColor: item.bg,
              backgroundImage:
                "radial-gradient(circle at 18% 16%, rgba(255,255,255,0.85) 0 18%, rgba(255,255,255,0) 19%), radial-gradient(circle at 88% 82%, rgba(255,255,255,0.7) 0 16%, rgba(255,255,255,0) 17%)",
              padding: "10px",
              boxShadow: `0 8px 18px ${item.shadow}`,
              borderBottom: `4px solid ${item.accent}`,
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
                marginBottom: "8px",
              }}
            >
              {item.icon}
            </div>
            <div style={{ fontSize: "14px", fontWeight: 700, color: item.color }}>{item.label}</div>
            <div style={{ fontSize: "17px", lineHeight: 1, fontWeight: 700, color: item.color, marginTop: "4px" }}>
              {Number(summary?.[item.key] || 0)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_BG_STYLE = {
  minHeight: "100vh",
  background: "#f5f7fb",
};

const PAGE_SHELL_STYLE = {
  maxWidth: "1560px",
  margin: "0 auto",
  padding: "12px 16px 28px",
};

const TOP_BAR_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "14px",
};

const TITLE_COPY_STYLE = {
  display: "grid",
  gap: "4px",
};

const SECTION_CARD_STYLE = {
  borderRadius: "16px",
  border: "1px solid #e4e8f0",
  background: "#ffffff",
  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
};

const SECTION_PADDING_STYLE = {
  padding: "24px 26px",
};

const SEARCH_INPUT_STYLE = {
  width: "100%",
  minHeight: "44px",
  borderRadius: "12px",
  border: "1px solid #c8d1e2",
  background: "#ffffff",
  color: "#1f2a44",
  fontSize: "15px",
  padding: "0 16px 0 44px",
  boxSizing: "border-box",
  outline: "none",
};

const SEARCH_ROW_STYLE = {
  position: "relative",
};

const SEARCH_ICON_STYLE = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6c7890",
  pointerEvents: "none",
};

const CONTROL_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  flexWrap: "wrap",
};

const FILTER_INPUT_STYLE = {
  width: "100%",
  minHeight: "42px",
  borderRadius: "10px",
  border: "1px solid #c8d1e2",
  background: "#ffffff",
  color: "#1f2a44",
  fontSize: "15px",
  padding: "0 14px",
  boxSizing: "border-box",
  outline: "none",
  appearance: "none",
};

const LIST_PANEL_STYLE = {
  borderRadius: "18px",
  border: "1px solid #dde4ef",
  background: "#ffffff",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
};

const LIST_HEADER_STYLE = {
  padding: "24px 28px 16px",
  borderBottom: "1px solid #e9edf5",
};

const ROW_CARD_STYLE = {
  margin: "0 16px 14px",
  padding: "18px 16px",
  borderRadius: "14px",
  border: "1px solid #dfe6f2",
  background: "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
};

const PRODUCT_META_STYLE = {
  display: "grid",
  gap: "4px",
};

const PRODUCT_ID_STYLE = {
  color: "#1f2a44",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: 1.1,
};

const PRODUCT_NAME_STYLE = {
  color: "#1f2a44",
  fontSize: "15px",
  fontWeight: 700,
};

const PRODUCT_SUB_STYLE = {
  color: "#647086",
  fontSize: "13px",
  lineHeight: 1.2,
};

const ITEM_BADGE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "96px",
  padding: "8px 12px",
  borderRadius: "10px",
  fontSize: "13px",
  fontWeight: 700,
  lineHeight: 1,
};

const ITEM_BADGE_THEMES = {
  serial: { background: "#e6efff", color: "#1c64f2" },
  inventory: { background: "#ece7ff", color: "#6d4cf5" },
  lot: { background: "#fff1c8", color: "#b67a00" },
  non_inventory: { background: "#efeefe", color: "#6a58d6" },
};

const ACTION_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #cfd7e5",
  background: "#ffffff",
  color: "#1f2a44",
  borderRadius: "10px",
  minHeight: "44px",
  padding: "0 18px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: 700,
};

const ICON_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #cfd7e5",
  background: "#ffffff",
  color: "#475569",
  borderRadius: "10px",
  width: "44px",
  height: "40px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.15s ease",
};

const FOOTER_STYLE = {
  padding: "18px 28px 20px",
  borderTop: "1px solid #e9edf5",
};

const FOOTER_NAV_BUTTON_STYLE = {
  ...ACTION_BUTTON_STYLE,
  minHeight: "40px",
  padding: "0 16px",
};

const FOOTER_NAV_BUTTON_DISABLED_STYLE = {
  ...FOOTER_NAV_BUTTON_STYLE,
  background: "#f1f3f7",
  color: "#a6afbf",
  cursor: "not-allowed",
};

const DASHBOARD_STACK_STYLE = {
  display: "grid",
  gap: "20px",
};

const HERO_STYLE = {
  padding: "28px 32px 24px",
  borderRadius: "18px",
  background: "linear-gradient(180deg, #0b1a39 0%, #0e2149 100%)",
  boxShadow: "0 18px 44px rgba(7, 15, 31, 0.18)",
  overflow: "hidden",
};

const HERO_INNER_STYLE = {
  display: "grid",
  gap: "22px",
  color: "#ffffff",
};

const HERO_TOP_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
};

const HERO_COPY_STYLE = {
  display: "grid",
  gap: "6px",
};

const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "18px",
};

const SUMMARY_TILE_STYLE = {
  padding: "22px 24px",
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 8px 20px rgba(10, 18, 38, 0.08)",
};

const KPI_ICON_STYLE = {
  width: "64px",
  height: "64px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const KPI_LABEL_STYLE = {
  color: "#5b657e",
  fontSize: "15px",
  fontWeight: 600,
};

const KPI_VALUE_STYLE = {
  color: "#17233f",
};

const KPI_META_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minWidth: 0,
};

function CubeGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="30" height="30" fill="none">
      <path d="m12 3 7 4v10l-7 4-7-4V7l7-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 3v14" stroke="currentColor" strokeWidth="2" />
      <path d="m5 7 7 4 7-4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function SerialGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="30" height="30" fill="none">
      <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M8 8v8M12 8v8M16 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LotGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="30" height="30" fill="none">
      <path d="M12 2 2 7l10 5 10-5-10-5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="m2 17 10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="m2 12 10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="none">
      <path d="M10.5 18a7.5 7.5 0 1 1 5.3-12.8A7.5 7.5 0 0 1 10.5 18Z" stroke="currentColor" strokeWidth="2" />
      <path d="m16 16 4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function EyeIcon({ size = 20 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20" fill="none">
      <path d="M12 3v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function NativeSearchInput({ value, onChange, placeholder }) {
  return (
    <div style={SEARCH_ROW_STYLE}>
      <span style={SEARCH_ICON_STYLE}>
        <SearchIcon />
      </span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={SEARCH_INPUT_STYLE}
      />
    </div>
  );
}

function NativeSelect({ value, onChange, options, ariaLabel }) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={FILTER_INPUT_STYLE}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function ItemTypeBadge({ itemType }) {
  const key = String(itemType || "").trim().toLowerCase();
  const theme = ITEM_BADGE_THEMES[key] || { background: "#eef2ff", color: "#4f46e5" };
  return <span style={{ ...ITEM_BADGE_STYLE, ...theme }}>{formatItemTypeLabel(itemType)}</span>;
}

export default function ProductConfiguration({ onNavigate, license }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lockSelectedSearchResult, setLockSelectedSearchResult] = useState(false);
  const [configuredProducts, setConfiguredProducts] = useState(() => readConfiguredProducts());
  const [configuredSearchTerm, setConfiguredSearchTerm] = useState("");
  const [configuredItemTypeFilter, setConfiguredItemTypeFilter] = useState("all");
  const [configuredPage, setConfiguredPage] = useState(1);
  const [configuredPageSize, setConfiguredPageSize] = useState("5");
  const [showEnabledProductsSection, setShowEnabledProductsSection] = useState(true);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [quickViewData, setQuickViewData] = useState(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewPage, setQuickViewPage] = useState(1);
  const [quickViewPageSize, setQuickViewPageSize] = useState("5");
  const [quickViewSearchTerm, setQuickViewSearchTerm] = useState("");
  const [quickViewFilter, setQuickViewFilter] = useState("all");
  const [quickViewEditRates, setQuickViewEditRates] = useState(false);
  const [quickViewRates, setQuickViewRates] = useState({
    daily_rate: "",
    weekly_rate: "",
    monthly_rate: "",
  });

  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reauthUrl, setReauthUrl] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showRentalEditor, setShowRentalEditor] = useState(false);
  const [shopCurrencyCode, setShopCurrencyCode] = useState("USD");
  const [licenseExpired, setLicenseExpired] = useState(false);

  const isLicenseFlaggedExpired =
    Boolean(license?.remainingDays !== undefined && Number(license.remainingDays) <= 0) ||
    Boolean(license?.plan_active === false);

  const isExpired = licenseExpired || isLicenseFlaggedExpired;

  const [rentalEnabled, setRentalEnabled] = useState(false);
  const [itemType, setItemType] = useState("");
  const [, setItemTypeLocked] = useState(false);
  const [enabledRates, setEnabledRates] = useState({
    daily: true,
    weekly: true,
    monthly: true,
  });
  const [rates, setRates] = useState({
    daily_rate: "",
    weekly_rate: "",
    monthly_rate: "",
  });
  const [serialSummary, setSerialSummary] = useState({
    total: 0,
    available: 0,
    on_rent: 0,
    maintenance: 0,
    reserved: 0,
    sold: 0,
  });
  const [serialItems, setSerialItems] = useState([]);
  const [serialSearchTerm, setSerialSearchTerm] = useState("");
  const [serialFilter, setSerialFilter] = useState("all");
  const [serialPage, setSerialPage] = useState(1);
  const [pageSize, setPageSize] = useState("5");
  const [showAddSerialForm, setShowAddSerialForm] = useState(false);
  const [showAddFormFromQuickView, setShowAddFormFromQuickView] = useState(false);
  const [addItemError, setAddItemError] = useState("");
  const [editingSerialId, setEditingSerialId] = useState("");
  const [deletingSerialId, setDeletingSerialId] = useState("");
  const [serialDetails, setSerialDetails] = useState({
    id: "",
    ns_id: "",
    serial_name: "",
    serial_number: "",
    stock: "1",
    rental_status: "Available",
    item_status: "Active",
    visibility: "visible",
  });
  const [showBulkCountForm, setShowBulkCountForm] = useState(false);
  const [showBulkImportForm, setShowBulkImportForm] = useState(false);
  const [showBulkFormFromQuickView, setShowBulkFormFromQuickView] = useState(false);
  const [bulkCountInput, setBulkCountInput] = useState("1");
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkImportError, setBulkImportError] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkCsvFileName, setBulkCsvFileName] = useState("");

  const isLotMode = itemType === "lot";
  const currencySymbol = useMemo(() => getCurrencySymbol(shopCurrencyCode), [shopCurrencyCode]);
  const [quickViewAddFormHostNode, setQuickViewAddFormHostNode] = useState(null);
  const bodyElement = typeof document !== "undefined" ? document.body : null;
  const renderSerialFormInQuickView =
    showAddFormFromQuickView && !!quickViewAddFormHostNode;
  const serialFormPortalTarget =
    renderSerialFormInQuickView ? quickViewAddFormHostNode : bodyElement;
  const renderBulkFormInQuickView =
    showBulkFormFromQuickView && !!quickViewAddFormHostNode;
  const bulkFormPortalTarget =
    renderBulkFormInQuickView ? quickViewAddFormHostNode : bodyElement;
  const setQuickViewAddFormHostRef = useCallback((node) => {
    setQuickViewAddFormHostNode(node || null);
  }, []);

  const triggerReauth = useCallback((data) => {
    const url = buildReauthUrl(extractReauthUrl(data));
    if (!url) return false;
    setReauthUrl(url);
    setError("Shopify re-authentication required. Please open the reconnect link in a new tab.");
    return true;
  }, []);

  const openReconnect = useCallback(() => {
    if (!reauthUrl) return;
    const opened = window.open(reauthUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      window.location.href = reauthUrl;
    }
  }, [reauthUrl]);

  const closeSerialForm = () => {
    setShowAddSerialForm(false);
    setShowAddFormFromQuickView(false);
    setEditingSerialId("");
    setAddItemError("");
  };

  const createEmptySerialItem = () => ({
    id: "",
    ns_id: "",
    serial_name: "",
    serial_number: "",
    stock: "1",
    rental_status: "Available",
    item_status: "Active",
    visibility: "visible",
  });

  const closeBulkForms = () => {
    setShowBulkCountForm(false);
    setShowBulkImportForm(false);
    setShowBulkFormFromQuickView(false);
    setBulkImportError("");
    setBulkRows([]);
    setBulkCountInput("1");
    setBulkCsvFileName("");
  };

  const resetSelectedProductView = () => {
    setSearchTerm("");
    setSelectedProduct(null);
    setShowEnabledProductsSection(true);
    setLockSelectedSearchResult(false);
    setSearchResults([]);
    setSearching(false);
    setLoadingConfig(false);
    setSaving(false);
    setError("");
    setReauthUrl("");
    setSuccessMessage("");
    setShowRentalEditor(false);
    setRentalEnabled(false);
    setItemType("");
    setItemTypeLocked(false);
    closeSerialForm();
    closeBulkForms();
    setDeletingSerialId("");
    setQuickViewOpen(false);
    setQuickViewProduct(null);
    setQuickViewData(null);
    setQuickViewLoading(false);
    setQuickViewEditRates(false);
    setQuickViewPage(1);
    setQuickViewSearchTerm("");
    setQuickViewFilter("all");
    setQuickViewRates({
      daily_rate: "",
      weekly_rate: "",
      monthly_rate: "",
    });
    setRates({
      daily_rate: "",
      weekly_rate: "",
      monthly_rate: "",
    });
    setEnabledRates({
      daily: false,
      weekly: false,
      monthly: false,
    });
    setSerialSummary({
      total: 0,
      available: 0,
      on_rent: 0,
      maintenance: 0,
      reserved: 0,
      sold: 0,
    });
    setSerialItems([]);
    setSerialSearchTerm("");
    setSerialFilter("all");
    setSerialPage(1);
    setEditingSerialId("");
    setSerialDetails(createEmptySerialItem());
  };

  const handleSearchTermChange = (value) => {
    const nextValue = String(value || "");
    if (!nextValue.trim()) {
      setSearchTerm("");
      resetSelectedProductView();
      return;
    }

    setLockSelectedSearchResult(false);
    setSearchTerm(nextValue);
  };

  const parseCsvText = (text) => {
    const lines = String(text || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .filter((line) => String(line || "").trim().length > 0);

    if (!lines.length) return [];

    const parseLine = (line) => {
      const out = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === "," && !inQuotes) {
          out.push(current.trim());
          current = "";
        } else {
          current += ch;
        }
      }
      out.push(current.trim());
      return out.map((v) => v.replace(/^"(.*)"$/, "$1"));
    };

    const headers = parseLine(lines[0]).map((h) =>
      String(h || "")
        .toLowerCase()
        .replace(/[\s\-]+/g, "_")
        .trim(),
    );

    const idxOf = (names) => headers.findIndex((h) => names.includes(h));
    const hNsId = idxOf(["ns_id", "nsid", "ns"]);
    const hName = idxOf(["serial_name", "lot_name", "name", "s_name"]);
    const hNumber = idxOf(["serial_number", "lot_number", "number", "s_number"]);
    const hStock = idxOf(["stock", "qty", "quantity"]);
    const hRentalStatus = idxOf(["rental_status", "rentalstatus", "status"]);
    const hItemStatus = idxOf(["item_status", "itemstatus"]);
    const hVisibility = idxOf(["visibility", "visible"]);

    const rows = [];
    for (let i = 1; i < lines.length; i += 1) {
      const cells = parseLine(lines[i]);
      const nsId = hNsId >= 0 ? String(cells[hNsId] || "").trim() : "";
      const serialNumber = hNumber >= 0 ? String(cells[hNumber] || "").trim() : "";
      const serialName = hName >= 0 ? String(cells[hName] || "").trim() : "";
      if (!nsId && !serialNumber && !serialName) continue;

      const stockRaw = hStock >= 0 ? String(cells[hStock] || "").trim() : "1";
      const rentalStatusRaw = hRentalStatus >= 0 ? String(cells[hRentalStatus] || "").trim() : "Available";
      const itemStatusRaw = hItemStatus >= 0 ? String(cells[hItemStatus] || "").trim() : "Active";
      const visibilityRaw = hVisibility >= 0 ? String(cells[hVisibility] || "").trim() : "visible";

      rows.push({
        ...createEmptySerialItem(),
        ns_id: nsId,
        serial_name: serialName,
        serial_number: serialNumber,
        stock: stockRaw || "1",
        rental_status: rentalStatusRaw || "Available",
        item_status: itemStatusRaw || "Active",
        visibility: String(visibilityRaw || "visible").toLowerCase() === "hidden" ? "hidden" : "visible",
      });
    }

    return rows;
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (lockSelectedSearchResult && selectedProduct) {
        setSearchResults([selectedProduct]);
        setSearching(false);
        return;
      }

      const term = String(searchTerm || "").trim();
      if (!term) {
        setSearchResults([]);
        setSearching(false);
        return;
      }

      const shop = getShopFromUrl();
      const shopParam = shop ? `&shop=${encodeURIComponent(shop)}` : "";

      setSearching(true);
      setError("");
      setReauthUrl("");
      try {
        const res = await fetch(
          `/api/admin/products/search?q=${encodeURIComponent(term)}&limit=12${shopParam}`,
          { credentials: "include" },
        );

        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }

        if (!res.ok) {
          if (triggerReauth(data)) {
            setSearchResults([]);
            return;
          }
          throw new Error(data?.detail || data?.message || "Failed to search products");
        }

        setSearchResults(Array.isArray(data?.products) ? data.products : []);
      } catch (e) {
        setSearchResults([]);
        setError(e?.message || "Failed to search products");
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, lockSelectedSearchResult, selectedProduct, triggerReauth]);

  useEffect(() => {
    const loadShopInfo = async () => {
      try {
        const shop = getShopFromUrl();
        const shopParam = shop ? `?shop=${encodeURIComponent(shop)}` : "";
        setReauthUrl("");
        const res = await fetch(`/api/admin/shop-info${shopParam}`, { credentials: "include" });
        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        if (!res.ok) {
          triggerReauth(data);
          return;
        }
        const code = String(data?.currencyCode || "").trim().toUpperCase();
        if (code) setShopCurrencyCode(code);
      } catch {
        // keep fallback currency
      }
    };

    loadShopInfo();
  }, [triggerReauth]);

  useEffect(() => {
    const loadConfiguredProducts = async () => {
      try {
        const shop = getShopFromUrl();
        const shopParam = shop ? `?shop=${encodeURIComponent(shop)}` : "";
        setReauthUrl("");
        const res = await fetch(`/api/admin/configured-products${shopParam}`, {
          credentials: "include",
        });
        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        if (!res.ok) {
          triggerReauth(data);
          return;
        }

        const products = Array.isArray(data?.products) ? data.products : [];
        setConfiguredProducts(products);
        writeConfiguredProducts(products);
      } catch {
        // keep local cache fallback
      }
    };

    loadConfiguredProducts();
  }, [triggerReauth]);

  const canSave = useMemo(() => {
    if (!selectedProduct) return false;
    if (!rentalEnabled) return true;
    if (!itemType) return false;
    if (itemType === "non_inventory") return true;
    if (enabledRates.daily && !rates.daily_rate) return false;
    if (enabledRates.weekly && !rates.weekly_rate) return false;
    if (enabledRates.monthly && !rates.monthly_rate) return false;
    return true;
  }, [selectedProduct, rentalEnabled, itemType, enabledRates, rates]);

  const filteredConfiguredProducts = useMemo(() => {
    const term = String(configuredSearchTerm || "").trim().toLowerCase();
    return configuredProducts.filter((row) => {
      const id = String(row?.id || "").toLowerCase();
      const name = String(row?.name || "").toLowerCase();
      const sku = String(getSkuDisplay(row) || "").toLowerCase();
      const type = String(row?.itemType || "").toLowerCase();

      const searchMatch = !term || id.includes(term) || name.includes(term) || sku.includes(term);
      const typeMatch = configuredItemTypeFilter === "all" || type === configuredItemTypeFilter;
      return searchMatch && typeMatch;
    });
  }, [configuredProducts, configuredSearchTerm, configuredItemTypeFilter]);

  const configuredTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredConfiguredProducts.length / Number(configuredPageSize || 10))),
    [filteredConfiguredProducts.length, configuredPageSize],
  );

  const pagedConfiguredProducts = useMemo(
    () =>
      filteredConfiguredProducts.slice(
        (configuredPage - 1) * Number(configuredPageSize || 10),
        configuredPage * Number(configuredPageSize || 10),
      ),
    [filteredConfiguredProducts, configuredPage, configuredPageSize],
  );

  const filteredSerialItems = useMemo(() => {
    const term = String(serialSearchTerm || "").trim().toLowerCase();
    return [...serialItems]
      .reverse()
      .filter((item) => {
        const nsId = String(item?.ns_id || "").toLowerCase();
        const serialName = String(item?.serial_name || "").toLowerCase();
        const serialNumber = String(item?.serial_number || "").toLowerCase();
        const rentalStatus = String(item?.rental_status || "").toLowerCase();
        const itemStatus = String(item?.item_status || "").toLowerCase();
        const visibility = String(item?.visibility || "").toLowerCase();

        const searchMatch =
          !term ||
          nsId.includes(term) ||
          serialName.includes(term) ||
          serialNumber.includes(term);

        const filterMatch =
          serialFilter === "all" ||
          rentalStatus === serialFilter ||
          itemStatus === serialFilter ||
          visibility === serialFilter;

        return searchMatch && filterMatch;
      });
  }, [serialItems, serialSearchTerm, serialFilter]);

  const quickViewSerialItems = useMemo(
    () => (Array.isArray(quickViewData?.serialItems) ? quickViewData.serialItems : []),
    [quickViewData],
  );

  const filteredQuickViewSerialItems = useMemo(() => {
    const term = String(quickViewSearchTerm || "").trim().toLowerCase();
    return quickViewSerialItems.filter((item) => {
      const nsId = String(item?.ns_id || "").toLowerCase();
      const serialName = String(item?.serial_name || "").toLowerCase();
      const serialNumber = String(item?.serial_number || "").toLowerCase();
      const rentalStatus = String(item?.rental_status || "").toLowerCase();
      const itemStatus = String(item?.item_status || "").toLowerCase();
      const visibility = String(item?.visibility || "").toLowerCase();

      const searchMatch =
        !term ||
        nsId.includes(term) ||
        serialName.includes(term) ||
        serialNumber.includes(term);

      const filterMatch =
        quickViewFilter === "all" ||
        rentalStatus === quickViewFilter ||
        itemStatus === quickViewFilter ||
        visibility === quickViewFilter;

      return searchMatch && filterMatch;
    });
  }, [quickViewSerialItems, quickViewSearchTerm, quickViewFilter]);

  const itemLabel = useMemo(() => {
    if (itemType === "lot") return "Lot";
    if (itemType === "serial") return "Serial";
    return "Item";
  }, [itemType]);

  const quickViewItemLabel = useMemo(() => {
    const quickType = String(quickViewData?.itemType || "").toLowerCase();
    if (quickType === "lot") return "Lot";
    if (quickType === "serial") return "Serial";
    return "Item";
  }, [quickViewData]);

  const quickViewTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredQuickViewSerialItems.length / Number(quickViewPageSize || 10))),
    [filteredQuickViewSerialItems.length, quickViewPageSize],
  );

  const quickViewPagedRows = useMemo(
    () =>
      filteredQuickViewSerialItems.slice(
        (quickViewPage - 1) * Number(quickViewPageSize || 10),
        quickViewPage * Number(quickViewPageSize || 10),
      ),
    [filteredQuickViewSerialItems, quickViewPage, quickViewPageSize],
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredSerialItems.length / Number(pageSize || 10))),
    [filteredSerialItems.length, pageSize],
  );

  const pagedSerialItems = useMemo(
    () =>
      filteredSerialItems.slice(
        (serialPage - 1) * Number(pageSize || 10),
        serialPage * Number(pageSize || 10),
      ),
    [filteredSerialItems, serialPage, pageSize],
  );

  useEffect(() => {
    setSerialPage(1);
  }, [serialSearchTerm, serialFilter, selectedProduct?.id, pageSize]);

  useEffect(() => {
    setConfiguredPage(1);
  }, [configuredSearchTerm, configuredItemTypeFilter, configuredPageSize]);

  useEffect(() => {
    if (configuredPage > configuredTotalPages) {
      setConfiguredPage(configuredTotalPages);
    }
  }, [configuredPage, configuredTotalPages]);

  useEffect(() => {
    if (serialPage > totalPages) {
      setSerialPage(totalPages);
    }
  }, [serialPage, totalPages]);

  useEffect(() => {
    setQuickViewPage(1);
  }, [quickViewData, quickViewPageSize, quickViewSearchTerm, quickViewFilter]);

  useEffect(() => {
    if (quickViewPage > quickViewTotalPages) {
      setQuickViewPage(quickViewTotalPages);
    }
  }, [quickViewPage, quickViewTotalPages]);

  useEffect(() => {
    if (itemType !== "non_inventory") return;
    setRates({
      daily_rate: "",
      weekly_rate: "",
      monthly_rate: "",
    });
    setEnabledRates({
      daily: false,
      weekly: false,
      monthly: false,
    });
  }, [itemType]);

  useEffect(() => {
    const term = String(searchTerm || "").trim().toLowerCase();
    if (!term || searching || lockSelectedSearchResult || !searchResults.length) return;

    const exactConfiguredMatch = searchResults.find((product) => {
      if (!isConfiguredRentalProduct(product)) return false;
      const title = String(product?.title || "").trim().toLowerCase();
      const sku = String(product?.sku || "").trim().toLowerCase();
      return title === term || (sku && sku === term);
    });

    if (!exactConfiguredMatch) return;

    const currentSelectedId = normalizeId(selectedProduct?.id || selectedProduct?.numericId || "");
    const nextMatchId = normalizeId(exactConfiguredMatch?.id || exactConfiguredMatch?.numericId || "");
    if (currentSelectedId && currentSelectedId === nextMatchId) return;

    void selectProduct(exactConfiguredMatch);
  }, [searchTerm, searching, lockSelectedSearchResult, searchResults, selectedProduct, configuredProducts]);

  const loadProductConfig = async (product) => {
    if (!product) return;
    setSuccessMessage("");
    setError("");
    setReauthUrl("");

    setLoadingConfig(true);
    try {
      const numericProductId = normalizeId(product?.id || product?.numericId || "");
      const shop = getShopFromUrl();
      const shopParam = shop ? `?shop=${encodeURIComponent(shop)}` : "";
      if (!numericProductId) {
        throw new Error("Invalid product selection");
      }

      const res = await fetch(`/api/admin/config-by-product/${numericProductId}${shopParam}`, {
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) {
        if (triggerReauth(data)) return;
        throw new Error(data?.message || "Failed to load product configuration");
      }

      setRentalEnabled(Boolean(data?.rentalEnabled));
      const loadedItemType = String(data?.itemType || "");
      setItemType(loadedItemType);
      setItemTypeLocked(Boolean(loadedItemType));

      setRates({
        daily_rate: String(data?.rates?.daily_rate || ""),
        weekly_rate: String(data?.rates?.weekly_rate || ""),
        monthly_rate: String(data?.rates?.monthly_rate || ""),
      });

      setEnabledRates({
        daily: Boolean(data?.rates?.daily_rate),
        weekly: Boolean(data?.rates?.weekly_rate),
        monthly: Boolean(data?.rates?.monthly_rate),
      });
      setSerialSummary({
        total: Number(data?.serialSummary?.total || 0),
        available: Number(data?.serialSummary?.available || 0),
        on_rent: Number(data?.serialSummary?.on_rent || 0),
        maintenance: Number(data?.serialSummary?.maintenance || 0),
        reserved: Number(data?.serialSummary?.reserved || 0),
        sold: Number(data?.serialSummary?.sold || 0),
      });
      setSerialItems(Array.isArray(data?.serialItems) ? data.serialItems : []);
    } catch (e) {
      setError(e?.message || "Failed to load configuration");
    } finally {
      setLoadingConfig(false);
    }
  };

  const updateConfiguredProducts = (product, enabled, selectedItemType = "") => {
    const id = normalizeId(product?.id || product?.numericId || "");
    if (!id) return;

    const next = [...configuredProducts];
    const idx = next.findIndex((p) => normalizeId(p?.id || "") === id);

    if (!enabled) {
      const filtered = next.filter((p) => normalizeId(p?.id || "") !== id);
      setConfiguredProducts(filtered);
      writeConfiguredProducts(filtered);
      return;
    }

    const record = {
      id,
      name: String(product?.title || ""),
      sku: String(product?.sku || "-"),
      variantNumericId: String(product?.variantNumericId || ""),
      itemType: String(selectedItemType || ""),
    };

    if (idx >= 0) {
      next[idx] = record;
    } else {
      next.unshift(record);
    }
    setConfiguredProducts(next);
    writeConfiguredProducts(next);
  };

  const exportConfiguredProducts = () => {
    if (!filteredConfiguredProducts.length) return;
    const headers = ["product_id", "product_name", "sku", "item_type"];
    const csvLines = [
      headers.join(","),
      ...filteredConfiguredProducts.map((row) =>
        [
          String(row?.id || ""),
          String(row?.name || ""),
          String(getSkuDisplay(row)),
          formatItemTypeLabel(String(row?.itemType || "")),
        ]
          .map((v) => `"${String(v).replace(/"/g, "\"\"")}"`)
          .join(","),
      ),
    ];

    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "enabled_rental_products.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isConfiguredRentalProduct = (product) => {
    const productId = normalizeId(product?.id || product?.numericId || "");
    if (!productId) return false;
    return configuredProducts.some((row) => normalizeId(row?.id || "") === productId);
  };

  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setShowEnabledProductsSection(false);
    setLockSelectedSearchResult(true);
    setSearchResults(product ? [product] : []);
    setSearchTerm(String(product?.title || product?.sku || ""));
    // Reset state so new product does not inherit previous product selections.
    setRentalEnabled(false);
    setItemType("");
    setItemTypeLocked(false);
    setShowAddSerialForm(false);
    setShowAddFormFromQuickView(false);
    setShowBulkCountForm(false);
    setShowBulkImportForm(false);
    setShowBulkFormFromQuickView(false);
    setBulkRows([]);
    setBulkImportError("");
    setBulkCountInput("1");
    setSerialDetails({
      id: "",
      ns_id: "",
      serial_name: "",
      serial_number: "",
      stock: "1",
      rental_status: "Available",
      item_status: "Active",
      visibility: "visible",
    });
    setRates({
      daily_rate: "",
      weekly_rate: "",
      monthly_rate: "",
    });
    setEnabledRates({
      daily: false,
      weekly: false,
      monthly: false,
    });
    setSerialSummary({
      total: 0,
      available: 0,
      on_rent: 0,
      maintenance: 0,
      reserved: 0,
      sold: 0,
    });
    setSerialItems([]);
    await loadProductConfig(product);
  };

  const saveConfiguration = async (rentalEnabledOverride = null) => {
    if (!selectedProduct) return;

    setSaving(true);
    setError("");
    setReauthUrl("");
    setSuccessMessage("");

    try {
      const variantNumericId = normalizeId(
        selectedProduct?.variantId || selectedProduct?.variantNumericId || "",
      );
      const productNumericId = normalizeId(
        selectedProduct?.id || selectedProduct?.numericId || "",
      );

      if (!productNumericId) {
        throw new Error("Product ID is required");
      }

      const effectiveRentalEnabled =
        typeof rentalEnabledOverride === "boolean" ? rentalEnabledOverride : rentalEnabled;

      const payload = {
        variantId: variantNumericId,
        productId: productNumericId,
        rentalEnabled: effectiveRentalEnabled,
        itemType,
        enabledRates,
        rates,
        serialItems: [],
      };

      const res = await fetch("/api/admin/config", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        if (triggerReauth(data)) return;
        throw new Error(data?.detail || data?.message || "Failed to save configuration");
      }

      setSuccessMessage("Rental options saved successfully.");
      updateConfiguredProducts(selectedProduct, !!effectiveRentalEnabled, itemType);
      if (effectiveRentalEnabled) {
        setShowEnabledProductsSection(true);
      }
    } catch (e) {
      setError(e?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const saveSerialItem = async () => {
    if (!selectedProduct) return;

    const variantNumericId = normalizeId(
      selectedProduct?.variantId || selectedProduct?.variantNumericId || "",
    );
    const productNumericId = normalizeId(
      selectedProduct?.id || selectedProduct?.numericId || "",
    );

    if (!productNumericId) {
      setAddItemError("Product ID is required");
      return;
    }

    if (!serialDetails.ns_id || !serialDetails.serial_number) {
      setAddItemError("NS ID and Serial Number are required.");
      return;
    }

    setSaving(true);
    setAddItemError("");
    setError("");
    setReauthUrl("");
    setSuccessMessage("");
    try {
      const effectiveType = isLotMode ? "lot" : "serial";
      const parsedStock = Number(serialDetails.stock);
      const safeStock = isLotMode ? (Number.isFinite(parsedStock) && parsedStock > 0 ? parsedStock : 1) : 1;

      const res = await fetch("/api/admin/config", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variantId: variantNumericId,
          productId: productNumericId,
          rentalEnabled,
          itemType: effectiveType,
          enabledRates,
          rates,
          serialDetails: {
            ...serialDetails,
            id: editingSerialId || serialDetails.id || "",
            stock: safeStock,
          },
          saveSerialItem: true,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (triggerReauth(data)) return;
        throw new Error(data?.detail || data?.message || "Failed to save serial item");
      }

      const wasFromQuickView = showAddFormFromQuickView;
      const quickViewCtx = quickViewProduct;
      const selectedCtx = selectedProduct;
      const successLabel = editingSerialId ? "updated" : "added";

      setSuccessMessage(`Serial item ${successLabel} successfully.`);
      closeSerialForm();
      setSerialDetails({
        id: "",
        ns_id: "",
        serial_name: "",
        serial_number: "",
        stock: "1",
        rental_status: "Available",
        item_status: "Active",
        visibility: "visible",
      });

      if (wasFromQuickView && quickViewCtx) {
        setQuickViewOpen(true);
        setQuickViewLoading(true);
        try {
          const refreshed = await fetchQuickViewData(quickViewCtx);
          if (refreshed) {
            setQuickViewData(refreshed);
            setQuickViewRates({
              daily_rate: String(refreshed?.rates?.daily_rate || ""),
              weekly_rate: String(refreshed?.rates?.weekly_rate || ""),
              monthly_rate: String(refreshed?.rates?.monthly_rate || ""),
            });
          }
        } catch (refreshError) {
          setError(refreshError?.message || "Failed to refresh quick view");
        } finally {
          setQuickViewLoading(false);
        }
      } else if (selectedCtx) {
        await loadProductConfig(selectedCtx);
      }
    } catch (e) {
      setAddItemError(e?.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const openEditSerialItem = (item) => {
    setShowAddFormFromQuickView(false);
    setAddItemError("");
    setEditingSerialId(String(item?.id || ""));
    setSerialDetails({
      id: String(item?.id || ""),
      ns_id: String(item?.ns_id || ""),
      serial_name: String(item?.serial_name || ""),
      serial_number: String(item?.serial_number || ""),
      stock: String(item?.stock || 1),
      rental_status: String(item?.rental_status || "Available"),
      item_status: String(item?.item_status || "Active"),
      visibility: String(item?.visibility || "visible"),
    });
    setShowAddSerialForm(true);
  };

  const deleteSerialItem = async (itemId) => {
    if (!selectedProduct || !itemId) return;
    const variantNumericId = normalizeId(
      selectedProduct?.variantId || selectedProduct?.variantNumericId || "",
    );
    const productNumericId = normalizeId(
      selectedProduct?.id || selectedProduct?.numericId || "",
    );

    setDeletingSerialId(String(itemId));
    setError("");
    setReauthUrl("");
    setSuccessMessage("");
    try {
      const res = await fetch("/api/admin/serial-item/delete", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: Number(itemId),
          variantId: variantNumericId,
          productId: productNumericId,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (triggerReauth(data)) return;
        throw new Error(data?.detail || data?.message || "Failed to delete serial item");
      }

      setSuccessMessage("Serial item deleted.");
      await loadProductConfig(selectedProduct);
    } catch (e) {
      setError(e?.message || "Failed to delete serial item");
    } finally {
      setDeletingSerialId("");
    }
  };

  const fetchQuickViewData = async (productRow) => {
    const productId = normalizeId(productRow?.id || "");
    if (!productId) return null;

    const shop = getShopFromUrl();
    const shopParam = shop ? `?shop=${encodeURIComponent(shop)}` : "";
    const res = await fetch(`/api/admin/config-by-product/${productId}${shopParam}`, {
      credentials: "include",
    });
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    if (!res.ok) {
      if (triggerReauth(data)) return null;
      throw new Error(data?.detail || data?.message || "Failed to load quick view");
    }
    return data;
  };

  const openQuickView = async (productRow, options = {}) => {
    const {
      resetView = true,
      closeOnError = true,
    } = options;

    setQuickViewProduct(productRow);
    setQuickViewOpen(true);
    setQuickViewLoading(true);
    setReauthUrl("");
    if (resetView) {
      setQuickViewData(null);
      setQuickViewEditRates(false);
      setQuickViewPage(1);
      setQuickViewSearchTerm("");
      setQuickViewFilter("all");
    }
    try {
      const data = await fetchQuickViewData(productRow);
      if (!data) return;
      setQuickViewData(data);
      setQuickViewRates({
        daily_rate: String(data?.rates?.daily_rate || ""),
        weekly_rate: String(data?.rates?.weekly_rate || ""),
        monthly_rate: String(data?.rates?.monthly_rate || ""),
      });
    } catch (e) {
      setError(e?.message || "Failed to load quick view");
      if (closeOnError) {
        setQuickViewOpen(false);
      }
    } finally {
      setQuickViewLoading(false);
    }
  };

  const buildProductContextFromQuickView = () => {
    if (!quickViewProduct) return null;
    return {
      id: String(quickViewProduct.id || ""),
      numericId: String(quickViewProduct.id || ""),
      title: String(quickViewProduct.name || ""),
      sku: String(quickViewProduct.sku || ""),
      variantId: "",
      variantNumericId: "",
    };
  };

  const editFromQuickView = (item) => {
    // Keep Quick View open and layer item editor on top.
    // Ensure serial edit context is available even if saved config is incomplete.
    setRentalEnabled(true);
    setItemType(quickViewData?.itemType === "lot" ? "lot" : "serial");
    setItemTypeLocked(true);

    // Open editor on top of Quick View.
    window.setTimeout(() => {
      setShowAddFormFromQuickView(true);
      setAddItemError("");
      setEditingSerialId(String(item?.id || ""));
      setSerialDetails({
        id: String(item?.id || ""),
        ns_id: String(item?.ns_id || ""),
        serial_name: String(item?.serial_name || ""),
        serial_number: String(item?.serial_number || ""),
        stock: String(item?.stock || 1),
        rental_status: String(item?.rental_status || "Available"),
        item_status: String(item?.item_status || "Active"),
        visibility: String(item?.visibility || "visible"),
      });
      setShowAddSerialForm(true);
    }, 80);

    const ctx = buildProductContextFromQuickView();
    if (ctx) {
      setSelectedProduct(ctx);
      void loadProductConfig(ctx);
    }
  };

  const deleteFromQuickView = async (itemId) => {
    const ctx = buildProductContextFromQuickView();
    if (ctx) setSelectedProduct(ctx);
    await deleteSerialItem(itemId);
    if (quickViewProduct) {
      await openQuickView(quickViewProduct);
    }
  };

  const addFromQuickView = () => {
    // Keep Quick View open and layer "Add New Item" editor on top.
    setRentalEnabled(true);
    setItemType(quickViewData?.itemType === "lot" ? "lot" : "serial");
    setItemTypeLocked(true);

    window.setTimeout(() => {
      setShowAddFormFromQuickView(true);
      setAddItemError("");
      setEditingSerialId("");
      setSerialDetails({
        id: "",
        ns_id: "",
        serial_name: "",
        serial_number: "",
        stock: "1",
        rental_status: "Available",
        item_status: "Active",
        visibility: "visible",
      });
      setShowAddSerialForm(true);
    }, 80);

    const ctx = buildProductContextFromQuickView();
    if (ctx) {
      setSelectedProduct(ctx);
      void loadProductConfig(ctx);
    }
  };

  const openBulkCountFromMain = () => {
    setShowBulkFormFromQuickView(false);
    setBulkImportError("");
    setBulkCountInput("1");
    setShowBulkCountForm(true);
    setShowBulkImportForm(false);
  };

  const openBulkCountFromQuickView = () => {
    const ctx = buildProductContextFromQuickView();
    if (ctx) {
      setSelectedProduct(ctx);
      void loadProductConfig(ctx);
    }
    setShowBulkFormFromQuickView(true);
    setBulkImportError("");
    setBulkCountInput("1");
    setShowBulkCountForm(true);
    setShowBulkImportForm(false);
  };

  const proceedToBulkImport = () => {
    const parsedCount = Number(bulkCountInput);
    if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
      setBulkImportError("Please enter a valid item count greater than 0.");
      return;
    }
    if (parsedCount > 100) {
      setBulkImportError("Maximum 100 items allowed per bulk import.");
      return;
    }
    const nextRows = Array.from({ length: parsedCount }, () => createEmptySerialItem());
    setBulkRows(nextRows);
    setBulkImportError("");
    setShowBulkCountForm(false);
    setShowBulkImportForm(true);
  };

  const updateBulkRowField = (index, field, value) => {
    setBulkRows((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleBulkCsvUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setBulkImportError("");
    setBulkCsvFileName(file.name || "");

    try {
      const text = await file.text();
      const parsedRows = parseCsvText(text);
      if (!parsedRows.length) {
        throw new Error("CSV file is empty or does not contain valid rows.");
      }
      if (parsedRows.length > 100) {
        throw new Error("Maximum 100 items allowed per CSV import.");
      }

      setBulkRows(parsedRows);
      setShowBulkCountForm(false);
      setShowBulkImportForm(true);
      setBulkCountInput(String(parsedRows.length));
    } catch (e) {
      setBulkImportError(e?.message || "Failed to parse CSV file.");
    } finally {
      if (event?.target) {
        event.target.value = "";
      }
    }
  };

  const saveBulkImport = async () => {
    if (!selectedProduct) return;

    const productNumericId = normalizeId(
      selectedProduct?.id || selectedProduct?.numericId || "",
    );
    const variantNumericId = normalizeId(
      selectedProduct?.variantId || selectedProduct?.variantNumericId || "",
    );

    if (!productNumericId) {
      setBulkImportError("Product ID is required.");
      return;
    }
    if (!Array.isArray(bulkRows) || bulkRows.length === 0) {
      setBulkImportError("No items to import.");
      return;
    }

    for (let i = 0; i < bulkRows.length; i += 1) {
      const row = bulkRows[i];
      if (!String(row?.ns_id || "").trim() || !String(row?.serial_number || "").trim()) {
        setBulkImportError(`Row ${i + 1}: NS ID and ${itemLabel} Number are required.`);
        return;
      }
    }

    setBulkSaving(true);
    setBulkImportError("");
    setError("");
    setReauthUrl("");
    setSuccessMessage("");
    try {
      const effectiveType = isLotMode ? "lot" : "serial";
      for (const row of bulkRows) {
        const parsedStock = Number(row.stock);
        const safeStock = isLotMode ? (Number.isFinite(parsedStock) && parsedStock > 0 ? parsedStock : 1) : 1;

        const payload = {
          variantId: variantNumericId,
          productId: productNumericId,
          rentalEnabled,
          itemType: effectiveType,
          enabledRates,
          rates,
          serialDetails: {
            ...row,
            stock: safeStock,
          },
          saveSerialItem: true,
        };

        const res = await fetch("/api/admin/config", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          if (triggerReauth(data)) return;
          throw new Error(data?.detail || data?.message || "Failed to save bulk items");
        }
      }

      setSuccessMessage(`${bulkRows.length} ${itemLabel.toLowerCase()} item(s) imported successfully.`);
      const wasFromQuickView = showBulkFormFromQuickView;
      const quickViewCtx = quickViewProduct;
      const selectedCtx = selectedProduct;
      closeBulkForms();

      if (wasFromQuickView && quickViewCtx) {
        setQuickViewOpen(true);
        setQuickViewLoading(true);
        try {
          const refreshed = await fetchQuickViewData(quickViewCtx);
          if (refreshed) {
            setQuickViewData(refreshed);
            setQuickViewRates({
              daily_rate: String(refreshed?.rates?.daily_rate || ""),
              weekly_rate: String(refreshed?.rates?.weekly_rate || ""),
              monthly_rate: String(refreshed?.rates?.monthly_rate || ""),
            });
          }
        } catch (refreshError) {
          setError(refreshError?.message || "Failed to refresh quick view");
        } finally {
          setQuickViewLoading(false);
        }
      } else if (selectedCtx) {
        await loadProductConfig(selectedCtx);
      }
    } catch (e) {
      setBulkImportError(e?.message || "Failed to import bulk items");
    } finally {
      setBulkSaving(false);
    }
  };

  const exportQuickViewSerialItems = () => {
    if (!filteredQuickViewSerialItems.length) return;
    const rows = filteredQuickViewSerialItems;
    const headers = [
      "ns_id",
      "serial_name",
      "serial_number",
      "stock",
      "rental_status",
      "item_status",
      "visibility",
    ];

    const csvLines = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => {
            const value = String(r?.[h] ?? "");
            const escaped = value.replace(/"/g, "\"\"");
            return `"${escaped}"`;
          })
          .join(","),
      ),
    ];

    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = String(quickViewProduct?.name || "serial_items")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    a.download = `${safeName || "serial_items"}_details.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const saveQuickViewRates = async () => {
    if (!quickViewProduct || !quickViewData) return;

    const variantNumericId = normalizeId(
      quickViewProduct?.variantId || quickViewProduct?.variantNumericId || "",
    );
    const productNumericId = normalizeId(quickViewProduct?.id || "");
    if (!productNumericId) {
      setError("Product ID is required");
      return;
    }

    setSaving(true);
    setError("");
    setReauthUrl("");
    setSuccessMessage("");
    try {
      const res = await fetch("/api/admin/config", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variantId: variantNumericId,
          productId: productNumericId,
          rentalEnabled: true,
          itemType: String(quickViewData?.itemType || ""),
          enabledRates: { daily: true, weekly: true, monthly: true },
          rates: quickViewRates,
          serialItems: [],
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (triggerReauth(data)) return;
        throw new Error(data?.detail || data?.message || "Failed to save rates");
      }

      setQuickViewData((prev) => ({
        ...(prev || {}),
        rates: {
          daily_rate: String(quickViewRates.daily_rate || ""),
          weekly_rate: String(quickViewRates.weekly_rate || ""),
          monthly_rate: String(quickViewRates.monthly_rate || ""),
        },
      }));
      setQuickViewEditRates(false);
      setSuccessMessage("Rates updated successfully.");
    } catch (e) {
      setError(e?.message || "Failed to save rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page fullWidth>
      {isExpired && (
        <LicenseExpiredOverlay
          onNavigate={onNavigate}
          license={license}
          customMessage="Your NetScore Rental Management license has expired. Access to rental product configuration is currently paused."
        />
      )}
      <Box
        style={{
          ...PAGE_SHELL_STYLE,
          filter: isExpired ? "blur(6px)" : "none",
          pointerEvents: isExpired ? "none" : "auto",
          userSelect: isExpired ? "none" : "auto",
          transition: "filter 0.2s ease",
        }}
      >
        <Box style={DASHBOARD_STACK_STYLE}>
          <Box style={HERO_STYLE}>
            <BlockStack gap="400" style={HERO_INNER_STYLE}>
              <div style={HERO_TOP_STYLE}>
                <div style={HERO_COPY_STYLE}>
                  <div style={{ fontSize: "40px", fontWeight: 800, lineHeight: 1.05, color: "#ffffff" }}>
                    Rental Products
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "18px", lineHeight: 1.35 }}>
                    Configure rental products, rates, inventory and availability.
                  </div>
                </div>
              </div>

              <Box style={SUMMARY_GRID_STYLE}>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#eaf0ff", color: "#2563eb" }}>
                        <CubeGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Configured Products</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {configuredProducts.length}
                        </div>
                      </div>
                    </InlineStack>
                  </InlineStack>
                </Box>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#e3f9e7", color: "#16a34a" }}>
                        <SerialGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Serialized Items</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {configuredProducts.filter((p) => (p.itemType || "").toLowerCase() === "serial").length}
                        </div>
                      </div>
                    </InlineStack>
                  </InlineStack>
                </Box>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#ffecdc", color: "#f97316" }}>
                        <LotGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Lot Numbered Items</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {configuredProducts.filter((p) => (p.itemType || "").toLowerCase() === "lot").length}
                        </div>
                      </div>
                    </InlineStack>
                  </InlineStack>
                </Box>
              </Box>
            </BlockStack>
          </Box>

          <BlockStack gap="400">
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">
                Select Product
              </Text>
              <NativeSearchInput
                value={searchTerm}
                onChange={handleSearchTermChange}
                placeholder="Search by product title or SKU"
              />
              <Text tone="subdued">Type product title or SKU to search.</Text>

              {searching && <Text tone="subdued">Searching products...</Text>}

              {!searching && !!searchTerm && searchResults.length === 0 && (
                <Text tone="subdued">No products found.</Text>
              )}

              {!searching && !!searchTerm && searchResults.length > 0 && (
                <BlockStack gap="200">
                  {searchResults.map((product) => {
                    const selected =
                      normalizeId(selectedProduct?.id) === normalizeId(product?.id);
                    return (
                      <Box
                        key={product.id}
                        style={{
                          padding: "14px 0",
                          borderTop: "1px solid #edf1f7",
                          background: selected ? "#f6f9ff" : "transparent",
                        }}
                      >
                        <InlineStack align="space-between" blockAlign="center" wrap={false}>
                          <BlockStack gap="050">
                            <Text as="p" variant="bodyMd" fontWeight="medium">
                              {product.title || "Untitled Product"}
                            </Text>
                            <Text as="p" tone="subdued" variant="bodySm">
                              SKU: {product.sku || "-"}
                            </Text>
                          </BlockStack>
                          <InlineStack gap="200" wrap={false}>
                            <Button onClick={() => selectProduct(product)}>
                              {selected ? "Selected" : "Select"}
                            </Button>
                            {selected ? (
                              <button
                                type="button"
                                onClick={resetSelectedProductView}
                                style={{
                                  border: "1px solid #dc2626",
                                  background: "#dc2626",
                                  color: "#ffffff",
                                  borderRadius: "10px",
                                  padding: "8px 14px",
                                  fontSize: "14px",
                                  lineHeight: 1.2,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                Unselect
                              </button>
                            ) : null}
                          </InlineStack>
                        </InlineStack>
                      </Box>
                    );
                  })}
                </BlockStack>
              )}
            </BlockStack>
          </Box>
        </Card>

        {configuredProducts.length > 0 && (!selectedProduct || showEnabledProductsSection) && (
          <Card>
            <Box padding="400">
              <BlockStack gap="350">
                <Text variant="headingMd" as="h2">
                  Enabled Rental Products
                </Text>
                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                  <InlineStack gap="200" wrap={false}>
                    <Box minWidth="280px">
                      <NativeSearchInput
                        value={configuredSearchTerm}
                        onChange={setConfiguredSearchTerm}
                        placeholder="Search Product ID / Name / SKU"
                      />
                    </Box>
                    <Box minWidth="170px">
                      <NativeSelect
                        ariaLabel="Item type filter"
                        value={configuredItemTypeFilter}
                        onChange={setConfiguredItemTypeFilter}
                        options={[
                          { label: "All Item Types", value: "all" },
                          { label: "Serialized Inventory Item", value: "serial" },
                          { label: "Inventory Item", value: "inventory" },
                          { label: "Lot Numbered Inventory Item", value: "lot" },
                          { label: "Non Inventory Item", value: "non_inventory" },
                        ]}
                      />
                    </Box>
                  </InlineStack>
                  <button
                    type="button"
                    onClick={exportConfiguredProducts}
                    disabled={!filteredConfiguredProducts.length}
                    style={ACTION_BUTTON_STYLE}
                  >
                    <span style={{ display: "inline-flex", marginRight: "10px", verticalAlign: "middle" }}>
                      <DownloadIcon />
                    </span>
                    Export
                  </button>
                </InlineStack>
                <Box style={{ ...LIST_PANEL_STYLE, marginTop: "8px" }}>
                  <BlockStack gap="0">
                    <Box padding="300" background="bg-surface-secondary" style={{ borderBottom: "1px solid #e9edf5" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.4fr) minmax(200px, 2.6fr) minmax(110px, 1.2fr) minmax(120px, 1.2fr) 70px", gap: "16px", alignItems: "center", padding: "0 16px" }}>
                        <div><Text fontWeight="medium">Product ID</Text></div>
                        <div><Text fontWeight="medium">Product Name</Text></div>
                        <div><Text fontWeight="medium">SKU</Text></div>
                        <div><Text fontWeight="medium">Item Type</Text></div>
                        <div style={{ textAlign: "center" }}><Text fontWeight="medium">Quick View</Text></div>
                      </div>
                    </Box>
                    <div
                      style={{
                        maxHeight: "340px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "14px 4px 4px 0",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#94a3b8 #f1f5f9",
                      }}
                    >
                      {pagedConfiguredProducts.map((row) => (
                        <Box key={row.id} style={ROW_CARD_STYLE}>
                          <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.4fr) minmax(200px, 2.6fr) minmax(110px, 1.2fr) minmax(120px, 1.2fr) 70px", gap: "16px", alignItems: "center" }}>
                            <div>
                              <div style={PRODUCT_META_STYLE}>
                                <div style={{ color: "#6b7280", fontSize: "13px", fontWeight: 600 }}>Product ID</div>
                                <div style={PRODUCT_ID_STYLE}>{row.id}</div>
                              </div>
                            </div>
                            <div>
                              <div style={PRODUCT_META_STYLE}>
                                <div style={{ color: "#6b7280", fontSize: "13px", fontWeight: 600 }}>Product Name</div>
                                <div style={PRODUCT_NAME_STYLE}>{row.name || "-"}</div>
                                <div style={PRODUCT_SUB_STYLE}>SKU: {getSkuDisplay(row)}</div>
                              </div>
                            </div>
                            <div>
                              <div style={PRODUCT_META_STYLE}>
                                <div style={{ color: "#6b7280", fontSize: "13px", fontWeight: 600 }}>SKU</div>
                                <div style={PRODUCT_SUB_STYLE}>{getSkuDisplay(row)}</div>
                              </div>
                            </div>
                            <div>
                              <div style={PRODUCT_META_STYLE}>
                                <div style={{ color: "#6b7280", fontSize: "13px", fontWeight: 600 }}>Item Type</div>
                                <ItemTypeBadge itemType={row.itemType} />
                              </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <button
                                type="button"
                                title="Quick View"
                                aria-label="Quick view"
                                onClick={() => openQuickView(row)}
                                style={ICON_BUTTON_STYLE}
                              >
                                <EyeIcon />
                              </button>
                            </div>
                          </div>
                        </Box>
                      ))}
                      {filteredConfiguredProducts.length === 0 && (
                        <Box padding="200">
                          <Text tone="subdued">No products match your search/filter.</Text>
                        </Box>
                      )}
                    </div>
                    {filteredConfiguredProducts.length > 0 && (
                      <Box style={{ ...FOOTER_STYLE, borderTop: "1px solid #e9edf5" }}>
                        <InlineStack align="space-between" blockAlign="center" wrap={false}>
                          <InlineStack gap="200" blockAlign="center" wrap={false}>
                            <Text tone="subdued">Rows per page:</Text>
                            <Box minWidth="100px">
                              <NativeSelect
                                ariaLabel="Rows per page"
                                value={configuredPageSize}
                                onChange={setConfiguredPageSize}
                                options={[
                                  { label: "5", value: "5" },
                                  { label: "10", value: "10" },
                                  { label: "20", value: "20" },
                                  { label: "50", value: "50" },
                                ]}
                              />
                            </Box>
                            <Text tone="subdued">
                              Page {configuredPage} of {configuredTotalPages}
                            </Text>
                          </InlineStack>
                          <InlineStack gap="200" wrap={false}>
                            <button
                              type="button"
                              disabled={configuredPage <= 1}
                              onClick={() => setConfiguredPage((p) => Math.max(1, p - 1))}
                              style={configuredPage <= 1 ? FOOTER_NAV_BUTTON_DISABLED_STYLE : FOOTER_NAV_BUTTON_STYLE}
                            >
                              Previous
                            </button>
                            <button
                              type="button"
                              disabled={configuredPage >= configuredTotalPages}
                              onClick={() => setConfiguredPage((p) => Math.min(configuredTotalPages, p + 1))}
                              style={configuredPage >= configuredTotalPages ? FOOTER_NAV_BUTTON_DISABLED_STYLE : FOOTER_NAV_BUTTON_STYLE}
                            >
                              Next
                            </button>
                          </InlineStack>
                        </InlineStack>
                      </Box>
                    )}
                  </BlockStack>
                </Box>
              </BlockStack>
            </Box>
          </Card>
        )}

        {selectedProduct && (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="headingMd" as="h2">
                    Selected Product
                  </Text>
                  <Badge tone="info">Ready</Badge>
                </InlineStack>

                <Text as="p">Product Name: {selectedProduct.title || "-"}</Text>
                <Text as="p">Product ID: {normalizeId(selectedProduct.id)}</Text>
                <Text as="p">Product SKU: {selectedProduct.sku || "-"}</Text>
              </BlockStack>
            </Box>
          </Card>
        )}

        {selectedProduct && (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "20px",
                    padding: "14px",
                    background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                      paddingBottom: "8px",
                      borderBottom: "1px dashed #d1d5db",
                    }}
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <span
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "10px",
                          background: "#e2e8f0",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#1e3a5f",
                          fontSize: "16px",
                        }}
                      >
                        {"\u{1F4CB}"}
                      </span>
                      <Text variant="headingMd" as="h2">
                        Available for rent
                      </Text>
                    </InlineStack>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span
                        style={{
                          background: rentalEnabled ? "#dcfce7" : "#fee2e2",
                          color: rentalEnabled ? "#166534" : "#991b1b",
                          borderRadius: "999px",
                          padding: "4px 12px",
                          fontWeight: 700,
                          fontSize: "18px",
                        }}
                      >
                        {rentalEnabled ? "Enabled" : "Disabled"}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const next = !rentalEnabled;
                          setRentalEnabled(next);

                          if (next) {
                            setEnabledRates({
                              daily: true,
                              weekly: true,
                              monthly: true,
                            });
                            return;
                          }

                          setShowRentalEditor(false);
                          await saveConfiguration(false);
                        }}
                        style={{
                          width: "52px",
                          height: "28px",
                          borderRadius: "999px",
                          border: "1px solid #cbd5e1",
                          background: rentalEnabled ? "#22c55e" : "#d1d5db",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        aria-label="Toggle rental options"
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: rentalEnabled ? "26px" : "2px",
                            width: "22px",
                            height: "22px",
                            borderRadius: "999px",
                            background: "white",
                            transition: "left 0.2s ease",
                          }}
                        />
                      </button>
                      {rentalEnabled && (
                        <button
                          type="button"
                          onClick={() => setShowRentalEditor((v) => !v)}
                          style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: "10px",
                            height: "30px",
                            padding: "0 10px",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          {showRentalEditor ? "Close Edit" : "Edit"}
                        </button>
                      )}
                    </div>
                  </div>

                  {rentalEnabled && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: itemType === "non_inventory" ? "1.2fr" : "1.2fr 1fr 1fr 1fr",
                        gap: "12px",
                        marginTop: "4px",
                      }}
                    >
                      <div style={{ border: "1px solid #dbeafe", borderRadius: "14px", padding: "12px", background: "#f8fbff", boxShadow: "0 4px 12px rgba(59,130,246,0.08)", borderBottom: "3px solid #3b82f6" }}>
                        <Text as="p" fontWeight="medium">Item Type:</Text>
                        {showRentalEditor ? (
                          <div style={{ marginTop: "8px" }}>
                            <Select
                              label=" "
                              value={itemType}
                              onChange={(v) => setItemType(v)}
                              disabled={Boolean(itemType)}
                              options={[
                                { label: "Select Item Type", value: "" },
                                { label: "Inventory", value: "inventory" },
                                { label: "Serial", value: "serial" },
                                { label: "Lot", value: "lot" },
                                { label: "Non Inventory", value: "non_inventory" },
                              ]}
                            />
                          </div>
                        ) : (
                          <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "16px" }}>{"\u{1F9FE}"}</span>
                            <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "999px", padding: "4px 10px", fontWeight: 700 }}>
                              {formatItemTypeLabel(itemType || "serial")}
                            </span>
                          </div>
                        )}
                      </div>

                      {itemType !== "non_inventory" && (
                      <div style={{ border: "1px solid #fde68a", borderRadius: "14px", padding: "12px", background: "#fffdf2", boxShadow: "0 4px 12px rgba(245,158,11,0.08)", borderBottom: "3px solid #f59e0b" }}>
                        <InlineStack gap="100" blockAlign="center">
                          <RateCalendarIcon color="#d97706" />
                          <Text as="p" fontWeight="medium">Daily:</Text>
                        </InlineStack>
                        {showRentalEditor ? (
                          <div style={{ marginTop: "6px" }}>
                            <TextField
                              label=" "
                              value={rates.daily_rate}
                              onChange={(v) => setRates((prev) => ({ ...prev, daily_rate: v }))}
                              autoComplete="off"
                              prefix={currencySymbol}
                            />
                          </div>
                        ) : (
                          <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                            {formatMoneyValue(rates.daily_rate, currencySymbol)}
                          </Text>
                        )}
                      </div>
                      )}

                      {itemType !== "non_inventory" && (
                      <div style={{ border: "1px solid #bfdbfe", borderRadius: "14px", padding: "12px", background: "#f8fbff", boxShadow: "0 4px 12px rgba(37,99,235,0.08)", borderBottom: "3px solid #3b82f6" }}>
                        <InlineStack gap="100" blockAlign="center">
                          <RateCalendarIcon color="#2563eb" />
                          <Text as="p" fontWeight="medium">Weekly:</Text>
                        </InlineStack>
                        {showRentalEditor ? (
                          <div style={{ marginTop: "6px" }}>
                            <TextField
                              label=" "
                              value={rates.weekly_rate}
                              onChange={(v) => setRates((prev) => ({ ...prev, weekly_rate: v }))}
                              autoComplete="off"
                              prefix={currencySymbol}
                            />
                          </div>
                        ) : (
                          <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                            {formatMoneyValue(rates.weekly_rate, currencySymbol)}
                          </Text>
                        )}
                      </div>
                      )}

                      {itemType !== "non_inventory" && (
                      <div style={{ border: "1px solid #86efac", borderRadius: "14px", padding: "12px", background: "#f7fff8", boxShadow: "0 4px 12px rgba(16,185,129,0.08)", borderBottom: "3px solid #10b981" }}>
                        <InlineStack gap="100" blockAlign="center">
                          <RateCalendarIcon color="#059669" />
                          <Text as="p" fontWeight="medium">Monthly:</Text>
                        </InlineStack>
                        {showRentalEditor ? (
                          <div style={{ marginTop: "6px" }}>
                            <TextField
                              label=" "
                              value={rates.monthly_rate}
                              onChange={(v) => setRates((prev) => ({ ...prev, monthly_rate: v }))}
                              autoComplete="off"
                              prefix={currencySymbol}
                            />
                          </div>
                        ) : (
                          <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                            {formatMoneyValue(rates.monthly_rate, currencySymbol)}
                          </Text>
                        )}
                      </div>
                      )}
                    </div>
                  )}

                </div>

                <InlineStack>
                  <Button
                    variant="primary"
                    onClick={saveConfiguration}
                    loading={saving || loadingConfig}
                    disabled={!canSave || saving || loadingConfig}
                  >
                    Save Rental Options
                  </Button>
                </InlineStack>
              </BlockStack>
            </Box>
          </Card>
        )}

        {selectedProduct && rentalEnabled && (itemType === "serial" || itemType === "lot") && (
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  {itemLabel} Items Summary
                </Text>

                <SerialSummaryCards summary={serialSummary} />

                <Divider />

                <Text variant="headingSm" as="h3">
                  {itemLabel} Item Details
                </Text>

                <InlineStack gap="200" align="space-between" blockAlign="center" wrap>
                  <InlineStack gap="200" wrap>
                    <Box minWidth="260px">
                      <TextField
                        label=" "
                        value={serialSearchTerm}
                        onChange={setSerialSearchTerm}
                        placeholder="Search NS ID / Name / Number"
                        autoComplete="off"
                      />
                    </Box>
                    <Box minWidth="140px">
                      <Select
                        label=" "
                        value={serialFilter}
                        onChange={setSerialFilter}
                        options={[
                          { label: "All", value: "all" },
                          { label: "Available", value: "available" },
                          { label: "On Rent", value: "on rent" },
                          { label: "Pending Fulfillment", value: "reserved" },
                          { label: "Maintenance", value: "maintenance" },
                          { label: "Sold", value: "sold" },
                        ]}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="200" wrap={false}>
                    <Button
                      onClick={() => {
                        setShowAddFormFromQuickView(false);
                        setAddItemError("");
                        setEditingSerialId("");
                        setSerialDetails(createEmptySerialItem());
                        setShowAddSerialForm(true);
                      }}
                      disabled={loadingConfig || !selectedProduct}
                    >
                      Add New Item
                    </Button>
                    <Button
                      onClick={openBulkCountFromMain}
                      disabled={loadingConfig || !selectedProduct}
                    >
                      Bulk Import
                    </Button>
                  </InlineStack>
                </InlineStack>

                {filteredSerialItems.length === 0 && (
                  <Text tone="subdued">No serial items found for this product.</Text>
                )}

                {filteredSerialItems.length > 0 && (
                  <Box overflowX="auto">
                    <BlockStack gap="0">
                      <Box padding="200" background="bg-surface-secondary">
                        <InlineStack gap="300" wrap={false}>
                          <Box minWidth="70px"><Text fontWeight="medium">NS ID</Text></Box>
                          <Box minWidth="70px"><Text fontWeight="medium">S.Name</Text></Box>
                          <Box minWidth="90px"><Text fontWeight="medium">S.Number</Text></Box>
                          <Box minWidth="50px"><Text fontWeight="medium">Stock</Text></Box>
                          <Box minWidth="100px"><Text fontWeight="medium">Rental Status</Text></Box>
                          <Box minWidth="95px"><Text fontWeight="medium">Item Status</Text></Box>
                          <Box minWidth="80px"><Text fontWeight="medium">Visibility</Text></Box>
                          <Box minWidth="120px"><Text fontWeight="medium">Actions</Text></Box>
                        </InlineStack>
                      </Box>
                      {pagedSerialItems.map((item) => (
                        <Box key={item.id || `${item.ns_id}-${item.serial_number}`} padding="200">
                          <InlineStack gap="300" wrap={false}>
                            <Box minWidth="70px"><Text>{item.ns_id || "-"}</Text></Box>
                            <Box minWidth="70px"><Text>{item.serial_name || "-"}</Text></Box>
                            <Box minWidth="90px"><Text>{item.serial_number || "-"}</Text></Box>
                            <Box minWidth="50px"><Text>{item.stock || 1}</Text></Box>
                            <Box minWidth="100px">
                              <StatusPill
                                kind={(() => {
                                  const rs = String(item.rental_status || "").toLowerCase();
                                  if (rs === "available") return "available";
                                  if (rs === "on rent") return "on_rent";
                                  if (rs === "reserved") return "reserved";
                                  if (rs === "maintenance") return "maintenance";
                                  if (rs === "sold") return "sold";
                                  return "sold";
                                })()}
                              >
                                {formatRentalStatusLabel(item.rental_status)}
                              </StatusPill>
                            </Box>
                            <Box minWidth="95px">
                              <StatusPill kind={String(item.item_status || "").toLowerCase() === "active" ? "item_active" : "item_inactive"}>
                                {item.item_status || "-"}
                              </StatusPill>
                            </Box>
                            <Box minWidth="80px">
                              <StatusPill kind={String(item.visibility || "").toLowerCase() === "visible" ? "visibility_visible" : "visibility_hidden"}>
                                {item.visibility || "-"}
                              </StatusPill>
                            </Box>
                            <Box minWidth="120px">
                              <InlineStack gap="200" wrap={false}>
                                <button
                                  type="button"
                                  title="Edit"
                                  aria-label="Edit serial item"
                                  onClick={() => openEditSerialItem(item)}
                                  style={{
                                    border: "1px solid #d1d5db",
                                    background: "#fff",
                                    borderRadius: "8px",
                                    width: "30px",
                                    height: "30px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  title="Delete"
                                  aria-label="Delete serial item"
                                  disabled={deletingSerialId === String(item.id || "")}
                                  onClick={() => deleteSerialItem(item.id)}
                                  style={{
                                    border: "1px solid #fecaca",
                                    background: "#fff5f5",
                                    borderRadius: "8px",
                                    width: "30px",
                                    height: "30px",
                                    cursor: deletingSerialId === String(item.id || "") ? "not-allowed" : "pointer",
                                    opacity: deletingSerialId === String(item.id || "") ? 0.5 : 1,
                                  }}
                                >
                                  🗑️
                                </button>
                              </InlineStack>
                            </Box>
                          </InlineStack>
                        </Box>
                      ))}
                      <Divider />
                      <Box padding="200">
                        <InlineStack align="space-between" blockAlign="center" wrap={false}>
                          <InlineStack gap="200" blockAlign="center" wrap={false}>
                            <Text tone="subdued">Rows per page</Text>
                            <Box minWidth="100px">
                              <Select
                                label=" "
                                value={pageSize}
                                onChange={setPageSize}
                                options={[{ label: "5", value: "5" }]}
                              />
                            </Box>
                            <Text tone="subdued">Page {serialPage} of {totalPages}</Text>
                          </InlineStack>
                          <InlineStack gap="200" wrap={false}>
                            <Button disabled={serialPage <= 1} onClick={() => setSerialPage((p) => Math.max(1, p - 1))}>
                              Previous
                            </Button>
                            <Button disabled={serialPage >= totalPages} onClick={() => setSerialPage((p) => Math.min(totalPages, p + 1))}>
                              Next
                            </Button>
                          </InlineStack>
                        </InlineStack>
                      </Box>
                    </BlockStack>
                  </Box>
                )}

              </BlockStack>
            </Box>
          </Card>
        )}

        {selectedProduct && rentalEnabled && itemType === "non_inventory" && (
          <Card>
            <Box padding="400">
              <p style={{ margin: 0, fontStyle: "italic", color: "#6b7280" }}>
                Inventory details are not available
              </p>
            </Box>
          </Card>
        )}

        {showAddSerialForm && serialFormPortalTarget && createPortal((
          <div
            style={{
              position: renderSerialFormInQuickView ? "absolute" : "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <div
              style={{
                width: "min(900px, 95vw)",
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 20px",
                  borderBottom: "1px solid #e1e3e5",
                }}
              >
                <Text variant="headingMd" as="h3">
                  Add {itemLabel} Item
                </Text>
                <Button
                  size="slim"
                  onClick={closeSerialForm}
                >
                  Close
                </Button>
              </div>

              <div style={{ padding: "18px 20px" }}>
                <BlockStack gap="200">
                  <Text tone="subdued">Fill in {itemLabel.toLowerCase()} item details and save.</Text>
                  <Divider />

                  <Text variant="headingSm" as="h3">
                    Item Info
                  </Text>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    <div>
                      <TextField
                        label="NS ID"
                        value={serialDetails.ns_id}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, ns_id: v }))}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <TextField
                        label={itemLabel === "Lot" ? "Lot Name" : "Serial Name"}
                        value={serialDetails.serial_name}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, serial_name: v }))}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <TextField
                        label={itemLabel === "Lot" ? "Lot Number" : "Serial Number"}
                        value={serialDetails.serial_number}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, serial_number: v }))}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <TextField
                        label="Stock"
                        type="number"
                        value={serialDetails.stock}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, stock: v }))}
                        disabled={!isLotMode}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    <div>
                      <Select
                        label="Rental Status"
                        value={serialDetails.rental_status}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, rental_status: v }))}
                        options={[
                          { label: "Available", value: "Available" },
                          { label: "Pending Fulfillment", value: "Reserved" },
                          { label: "On Rent", value: "On Rent" },
                          { label: "Maintenance", value: "Maintenance" },
                          { label: "Sold", value: "Sold" },
                        ]}
                      />
                    </div>
                    <div>
                      <Select
                        label="Item Status"
                        value={serialDetails.item_status}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, item_status: v }))}
                        options={[
                          { label: "Active", value: "Active" },
                          { label: "Inactive", value: "Inactive" },
                        ]}
                      />
                    </div>
                    <div>
                      <Select
                        label="Visibility"
                        value={serialDetails.visibility}
                        onChange={(v) => setSerialDetails((s) => ({ ...s, visibility: v }))}
                        options={[
                          { label: "Visible", value: "visible" },
                          { label: "Hidden", value: "hidden" },
                        ]}
                      />
                    </div>
                  </div>

                  {addItemError && (
                    <Box paddingBlockStart="100">
                      <Text tone="critical">{addItemError}</Text>
                    </Box>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginTop: "8px",
                    }}
                  >
                    <Button
                      onClick={closeSerialForm}
                    >
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={saveSerialItem} loading={saving}>
                      Save
                    </Button>
                  </div>
                </BlockStack>
              </div>
            </div>
          </div>
        ), serialFormPortalTarget)}

        {showBulkCountForm && bulkFormPortalTarget && createPortal((
          <div
            style={{
              position: renderBulkFormInQuickView ? "absolute" : "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <div
              style={{
                width: "min(460px, 95vw)",
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 20px",
                  borderBottom: "1px solid #e1e3e5",
                }}
              >
                <Text variant="headingMd" as="h3">
                  Bulk Import {itemLabel} Items
                </Text>
                <Button size="slim" onClick={closeBulkForms}>Close</Button>
              </div>
              <div style={{ padding: "18px 20px" }}>
                <BlockStack gap="300">
                  <Text tone="subdued">
                    Enter how many {itemLabel.toLowerCase()} items you want to add.
                  </Text>
                  <TextField
                    label="Number of items"
                    type="number"
                    value={bulkCountInput}
                    onChange={setBulkCountInput}
                    autoComplete="off"
                    min={1}
                    max={100}
                  />
                  <div
                    style={{
                      border: "1px dashed #cbd5e1",
                      borderRadius: "12px",
                      padding: "14px",
                      background: "#f8fafc",
                    }}
                  >
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h4">
                        CSV Upload
                      </Text>
                      <Text tone="subdued">
                        Upload a CSV instead of typing rows manually. Supported columns: ns_id,
                        serial_name or lot_name, serial_number or lot_number, stock,
                        rental_status, item_status, visibility.
                      </Text>
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleBulkCsvUpload}
                      />
                      {bulkCsvFileName ? (
                        <Text tone="subdued">Selected file: {bulkCsvFileName}</Text>
                      ) : null}
                    </BlockStack>
                  </div>
                  {bulkImportError && <Text tone="critical">{bulkImportError}</Text>}
                  <InlineStack align="end" gap="200">
                    <Button onClick={closeBulkForms}>Cancel</Button>
                    <Button variant="primary" onClick={proceedToBulkImport}>Next</Button>
                  </InlineStack>
                </BlockStack>
              </div>
            </div>
          </div>
        ), bulkFormPortalTarget)}

        {showBulkImportForm && bulkFormPortalTarget && createPortal((
          <div
            style={{
              position: renderBulkFormInQuickView ? "absolute" : "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <div
              style={{
                width: "min(1120px, 98vw)",
                maxHeight: "90vh",
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 20px",
                  borderBottom: "1px solid #e1e3e5",
                }}
              >
                <Text variant="headingMd" as="h3">
                  Enter {bulkRows.length} {itemLabel.toLowerCase()} item(s)
                </Text>
                <Button size="slim" onClick={closeBulkForms}>Close</Button>
              </div>
              <div style={{ padding: "18px 20px", overflow: "auto" }}>
                <BlockStack gap="300">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "12px 14px",
                      background: "#f8fafc",
                    }}
                  >
                    <BlockStack gap="100">
                      <Text variant="headingSm" as="h4">
                        Source
                      </Text>
                      <Text tone="subdued">
                        {bulkCsvFileName
                          ? `CSV loaded: ${bulkCsvFileName}`
                          : "Manual entry mode"}
                      </Text>
                    </BlockStack>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Text tone="subdued">{bulkRows.length} row(s)</Text>
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleBulkCsvUpload}
                      />
                    </div>
                  </div>
                  {bulkRows.map((row, index) => (
                    <div
                      key={`bulk-row-${index + 1}`}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        padding: "14px",
                        background: "#f9fafb",
                      }}
                    >
                      <BlockStack gap="200">
                        <Text variant="headingSm" as="h4">
                          Item {index + 1}
                        </Text>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                            gap: "12px",
                          }}
                        >
                          <TextField
                            label="NS ID"
                            value={row.ns_id}
                            onChange={(v) => updateBulkRowField(index, "ns_id", v)}
                            autoComplete="off"
                          />
                          <TextField
                            label={itemLabel === "Lot" ? "Lot Name" : "Serial Name"}
                            value={row.serial_name}
                            onChange={(v) => updateBulkRowField(index, "serial_name", v)}
                            autoComplete="off"
                          />
                          <TextField
                            label={itemLabel === "Lot" ? "Lot Number" : "Serial Number"}
                            value={row.serial_number}
                            onChange={(v) => updateBulkRowField(index, "serial_number", v)}
                            autoComplete="off"
                          />
                          <TextField
                            label="Stock"
                            type="number"
                            value={row.stock}
                            onChange={(v) => updateBulkRowField(index, "stock", v)}
                            disabled={!isLotMode}
                            autoComplete="off"
                          />
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: "12px",
                          }}
                        >
                          <Select
                            label="Rental Status"
                            value={row.rental_status}
                            onChange={(v) => updateBulkRowField(index, "rental_status", v)}
                            options={[
                              { label: "Available", value: "Available" },
                              { label: "On Rent", value: "On Rent" },
                              { label: "Maintenance", value: "Maintenance" },
                              { label: "Pending Fulfillment", value: "Reserved" },
                              { label: "Sold", value: "Sold" },
                            ]}
                          />
                          <Select
                            label="Item Status"
                            value={row.item_status}
                            onChange={(v) => updateBulkRowField(index, "item_status", v)}
                            options={[
                              { label: "Active", value: "Active" },
                              { label: "Inactive", value: "Inactive" },
                            ]}
                          />
                          <Select
                            label="Visibility"
                            value={row.visibility}
                            onChange={(v) => updateBulkRowField(index, "visibility", v)}
                            options={[
                              { label: "Visible", value: "visible" },
                              { label: "Hidden", value: "hidden" },
                            ]}
                          />
                        </div>
                      </BlockStack>
                    </div>
                  ))}

                  {bulkImportError && <Text tone="critical">{bulkImportError}</Text>}
                </BlockStack>
              </div>
              <div
                style={{
                  borderTop: "1px solid #e1e3e5",
                  padding: "14px 20px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <Button onClick={closeBulkForms}>Cancel</Button>
                <Button variant="primary" onClick={saveBulkImport} loading={bulkSaving}>
                  Save All
                </Button>
              </div>
            </div>
          </div>
        ), bulkFormPortalTarget)}

        {!!error && (
          <Card>
            <Box padding="300">
              <BlockStack gap="200">
                <Text tone="critical">{error}</Text>
                {!!reauthUrl && (
                  <InlineStack gap="200">
                    <Button variant="primary" onClick={openReconnect}>
                      Reconnect Shopify
                    </Button>
                    <Button url={reauthUrl} external>
                      Open In New Tab
                    </Button>
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        )}

        {!!successMessage && (
          <Card>
            <Box padding="300">
              <Text tone="success">{successMessage}</Text>
            </Box>
          </Card>
        )}

        <Modal
          open={quickViewOpen}
          onClose={() => {
            setQuickViewOpen(false);
            if (showAddFormFromQuickView) closeSerialForm();
            if (showBulkFormFromQuickView) closeBulkForms();
          }}
          title={`Quick View: ${quickViewProduct?.name || ""}`}
          size="large"
        >
          <Modal.Section>
            <div ref={setQuickViewAddFormHostRef} style={{ position: "relative" }}>
              {quickViewLoading && <Text tone="subdued">Loading...</Text>}
              {!quickViewLoading && quickViewData && (
                <BlockStack gap="300">
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "20px",
                    padding: "14px",
                    background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                      paddingBottom: "8px",
                      borderBottom: "1px dashed #d1d5db",
                    }}
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <span
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "10px",
                          background: "#e2e8f0",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#1e3a5f",
                          fontSize: "14px",
                        }}
                      >
                        {"\u{1F4CB}"}
                      </span>
                      <Text variant="headingSm" as="h3">Available for rent</Text>
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center">
                      <span
                        style={{
                          background: quickViewData?.rentalEnabled ? "#dcfce7" : "#fee2e2",
                          color: quickViewData?.rentalEnabled ? "#166534" : "#991b1b",
                          borderRadius: "999px",
                          padding: "4px 10px",
                          fontWeight: 700,
                        }}
                      >
                        {quickViewData?.rentalEnabled ? "Enabled" : "Disabled"}
                      </span>
                      {quickViewData?.rentalEnabled && String(quickViewData?.itemType || "") !== "non_inventory" && (
                        quickViewEditRates ? (
                          <InlineStack gap="100" wrap={false}>
                            <Button size="slim" onClick={() => setQuickViewEditRates(false)}>Cancel</Button>
                            <Button size="slim" variant="primary" onClick={saveQuickViewRates} loading={saving}>Save Rates</Button>
                          </InlineStack>
                        ) : (
                          <Button size="slim" onClick={() => setQuickViewEditRates(true)}>Edit Rates</Button>
                        )
                      )}
                    </InlineStack>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        String(quickViewData?.itemType || "") === "non_inventory"
                          ? "1.2fr"
                          : "1.2fr 1fr 1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div style={{ border: "1px solid #dbeafe", borderRadius: "14px", padding: "12px", background: "#f8fbff", boxShadow: "0 4px 12px rgba(59,130,246,0.08)", borderBottom: "3px solid #3b82f6" }}>
                      <Text as="p" fontWeight="medium">Item Type:</Text>
                      <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "16px" }}>{"\u{1F9FE}"}</span>
                        <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "999px", padding: "4px 10px", fontWeight: 700 }}>
                          {formatItemTypeLabel(quickViewData?.itemType || "serial")}
                        </span>
                      </div>
                    </div>
                    {String(quickViewData?.itemType || "") !== "non_inventory" && (
                    <div style={{ border: "1px solid #fde68a", borderRadius: "14px", padding: "12px", background: "#fffdf2", boxShadow: "0 4px 12px rgba(245,158,11,0.08)", borderBottom: "3px solid #f59e0b" }}>
                      <InlineStack gap="100" blockAlign="center">
                        <RateCalendarIcon color="#d97706" />
                        <Text as="p" fontWeight="medium">Daily:</Text>
                      </InlineStack>
                      {quickViewEditRates ? (
                        <div style={{ marginTop: "6px" }}>
                          <TextField
                            label=" "
                            value={quickViewRates.daily_rate}
                            onChange={(v) => setQuickViewRates((prev) => ({ ...prev, daily_rate: v }))}
                            autoComplete="off"
                            prefix={currencySymbol}
                          />
                        </div>
                      ) : (
                        <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                          {formatMoneyValue(quickViewData?.rates?.daily_rate, currencySymbol)}
                        </Text>
                      )}
                    </div>
                    )}
                    {String(quickViewData?.itemType || "") !== "non_inventory" && (
                    <div style={{ border: "1px solid #bfdbfe", borderRadius: "14px", padding: "12px", background: "#f8fbff", boxShadow: "0 4px 12px rgba(37,99,235,0.08)", borderBottom: "3px solid #3b82f6" }}>
                      <InlineStack gap="100" blockAlign="center">
                        <RateCalendarIcon color="#2563eb" />
                        <Text as="p" fontWeight="medium">Weekly:</Text>
                      </InlineStack>
                      {quickViewEditRates ? (
                        <div style={{ marginTop: "6px" }}>
                          <TextField
                            label=" "
                            value={quickViewRates.weekly_rate}
                            onChange={(v) => setQuickViewRates((prev) => ({ ...prev, weekly_rate: v }))}
                            autoComplete="off"
                            prefix={currencySymbol}
                          />
                        </div>
                      ) : (
                        <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                          {formatMoneyValue(quickViewData?.rates?.weekly_rate, currencySymbol)}
                        </Text>
                      )}
                    </div>
                    )}
                    {String(quickViewData?.itemType || "") !== "non_inventory" && (
                    <div style={{ border: "1px solid #86efac", borderRadius: "14px", padding: "12px", background: "#f7fff8", boxShadow: "0 4px 12px rgba(16,185,129,0.08)", borderBottom: "3px solid #10b981" }}>
                      <InlineStack gap="100" blockAlign="center">
                        <RateCalendarIcon color="#059669" />
                        <Text as="p" fontWeight="medium">Monthly:</Text>
                      </InlineStack>
                      {quickViewEditRates ? (
                        <div style={{ marginTop: "6px" }}>
                          <TextField
                            label=" "
                            value={quickViewRates.monthly_rate}
                            onChange={(v) => setQuickViewRates((prev) => ({ ...prev, monthly_rate: v }))}
                            autoComplete="off"
                            prefix={currencySymbol}
                          />
                        </div>
                      ) : (
                        <Text as="p" variant="heading2xl" fontWeight="bold" style={{ marginTop: "2px" }}>
                          {formatMoneyValue(quickViewData?.rates?.monthly_rate, currencySymbol)}
                        </Text>
                      )}
                    </div>
                    )}
                  </div>
                </div>

                {String(quickViewData?.itemType || "") === "non_inventory" ? (
                  <>
                    <Divider />
                    <p style={{ margin: 0, fontStyle: "italic", color: "#6b7280" }}>
                      Inventory details are not available
                    </p>
                  </>
                ) : (
                  <>
                <Divider />
                <Text variant="headingSm" as="h3">{quickViewItemLabel} Items Summary</Text>
                <SerialSummaryCards summary={quickViewData?.serialSummary || {}} hideReserved />

                {String(quickViewData?.itemType || "") !== "inventory" && (
                  <>
                <Divider />
                <Text variant="headingSm" as="h3">{quickViewItemLabel} Item Details</Text>
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <InlineStack gap="200" wrap>
                    <Box minWidth="260px">
                      <TextField
                        label=" "
                        value={quickViewSearchTerm}
                        onChange={setQuickViewSearchTerm}
                        placeholder="Search NS ID / Name / Number"
                        autoComplete="off"
                      />
                    </Box>
                    <Box minWidth="140px">
                      <Select
                        label=" "
                        value={quickViewFilter}
                        onChange={setQuickViewFilter}
                        options={[
                          { label: "All", value: "all" },
                          { label: "Available", value: "available" },
                          { label: "On Rent", value: "on rent" },
                          { label: "Pending Fulfillment", value: "reserved" },
                          { label: "Maintenance", value: "maintenance" },
                          { label: "Sold", value: "sold" },
                        ]}
                      />
                    </Box>
                  </InlineStack>
                  <InlineStack gap="200" wrap={false}>
                    <Button size="slim" onClick={addFromQuickView}>
                      Add New Item
                    </Button>
                    <Button size="slim" onClick={openBulkCountFromQuickView}>
                      Bulk Import
                    </Button>
                    <Button size="slim" onClick={exportQuickViewSerialItems} disabled={!filteredQuickViewSerialItems.length}>
                      Export
                    </Button>
                  </InlineStack>
                </InlineStack>
                {filteredQuickViewSerialItems.length > 0 ? (
                  <Box overflowX="auto">
                    <BlockStack gap="0">
                      <Box padding="150" background="bg-surface-secondary">
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "90px 100px 110px 60px 130px 110px 95px 130px",
                            gap: "10px",
                            minWidth: "900px",
                          }}
                        >
                          <Text fontWeight="medium">NS ID</Text>
                          <Text fontWeight="medium">S.Name</Text>
                          <Text fontWeight="medium">S.Number</Text>
                          <Text fontWeight="medium">Stock</Text>
                          <Text fontWeight="medium">Rental Status</Text>
                          <Text fontWeight="medium">Item Status</Text>
                          <Text fontWeight="medium">Visibility</Text>
                          <Text fontWeight="medium">Actions</Text>
                        </div>
                      </Box>
                      {quickViewPagedRows.map((item) => (
                        <Box key={item.id || `${item.ns_id}-${item.serial_number}`} padding="150">
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "90px 100px 110px 60px 130px 110px 95px 130px",
                              gap: "10px",
                              alignItems: "center",
                              minWidth: "900px",
                            }}
                          >
                            <Text>{item.ns_id || "-"}</Text>
                            <Text>{item.serial_name || "-"}</Text>
                            <Text>{item.serial_number || "-"}</Text>
                            <Text>{item.stock || 1}</Text>
                            <div>
                              <StatusPill
                                kind={(() => {
                                  const rs = String(item.rental_status || "").toLowerCase();
                                  if (rs === "available") return "available";
                                  if (rs === "on rent") return "on_rent";
                                  if (rs === "reserved") return "reserved";
                                  if (rs === "maintenance") return "maintenance";
                                  if (rs === "sold") return "sold";
                                  return "sold";
                                })()}
                              >
                                {formatRentalStatusLabel(item.rental_status)}
                              </StatusPill>
                            </div>
                            <div>
                              <StatusPill kind={String(item.item_status || "").toLowerCase() === "active" ? "item_active" : "item_inactive"}>
                                {item.item_status || "-"}
                              </StatusPill>
                            </div>
                            <div>
                              <StatusPill kind={String(item.visibility || "").toLowerCase() === "visible" ? "visibility_visible" : "visibility_hidden"}>
                                {item.visibility || "-"}
                              </StatusPill>
                            </div>
                            <InlineStack gap="200" wrap={false}>
                              <button
                                type="button"
                                title="Edit"
                                aria-label="Edit serial item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  editFromQuickView(item);
                                }}
                                style={{
                                  border: "1px solid #d1d5db",
                                  background: "#fff",
                                  borderRadius: "8px",
                                  width: "30px",
                                  height: "30px",
                                  cursor: "pointer",
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                aria-label="Delete serial item"
                                disabled={deletingSerialId === String(item.id || "")}
                                onClick={() => deleteFromQuickView(item.id)}
                                style={{
                                  border: "1px solid #fecaca",
                                  background: "#fff5f5",
                                  borderRadius: "8px",
                                  width: "30px",
                                  height: "30px",
                                  cursor: deletingSerialId === String(item.id || "") ? "not-allowed" : "pointer",
                                  opacity: deletingSerialId === String(item.id || "") ? 0.5 : 1,
                                }}
                              >
                                🗑️
                              </button>
                            </InlineStack>
                          </div>
                        </Box>
                      ))}
                      <Divider />
                      <Box padding="150">
                        <InlineStack align="space-between" blockAlign="center" wrap={false}>
                          <InlineStack gap="200" blockAlign="center" wrap={false}>
                            <Text tone="subdued">Rows per page</Text>
                            <Box minWidth="100px">
                              <Select
                                label=" "
                                value={quickViewPageSize}
                                onChange={setQuickViewPageSize}
                                options={[{ label: "5", value: "5" }]}
                              />
                            </Box>
                            <Text tone="subdued">Page {quickViewPage} of {quickViewTotalPages}</Text>
                          </InlineStack>
                          <InlineStack gap="200" wrap={false}>
                            <Button
                              disabled={quickViewPage <= 1}
                              onClick={() => setQuickViewPage((p) => Math.max(1, p - 1))}
                            >
                              Previous
                            </Button>
                            <Button
                              disabled={quickViewPage >= quickViewTotalPages}
                              onClick={() => setQuickViewPage((p) => Math.min(quickViewTotalPages, p + 1))}
                            >
                              Next
                            </Button>
                          </InlineStack>
                        </InlineStack>
                      </Box>
                    </BlockStack>
                  </Box>
                ) : (
                  <Text tone="subdued">No serial items found.</Text>
                )}
                  </>
                )}
                  </>
                )}
                </BlockStack>
              )}
            </div>
          </Modal.Section>
        </Modal>
          </BlockStack>
        </Box>
      </Box>
    </Page>
  );
}
